import {sortedJSONStringify} from "@cm/utils/sorted-json-stringify"
import type {SceneNodes} from "#template/interfaces/scene-object"
import {byOrder, configurationParts, configuratorOrder, type VariationPart} from "#template/variations/configuration"
import {
    checkKeysAreKnown,
    choosableGate,
    type ContextState,
    evaluateContext,
    permutableVariantIds,
    unassignedFreeKeys,
    type VariationFilter,
} from "#template/variations/fast-variations"
import type {VariationOracle} from "#template/variations/gather-all-variations"
import {compareScenes, type SceneEffect} from "#template/variations/scene-difference"
import type {VariationStructure} from "#template/variations/structure"

export type LayerGroupRole = "structure" | "geometry" | "scene" | "material" | "post-processing" | "no effect"

export type LayerPlanGroup = {
    key: string
    name: string
    // A group can play different roles in different contexts.
    roles: LayerGroupRole[]
    // Per role: the groups a structure group decides about, the objects a material group recolours, or what changed geometry or the scene.
    reasons: Partial<Record<LayerGroupRole, string[]>>
    // What a structure group's options change themselves, with the groups below them at their defaults; its reasons are under these roles.
    structureEffects: LayerGroupRole[]
    effects: SceneEffect[]
}

export type LayerPlanLayer = {
    key: string
    // Absent for the context's rest layer.
    group?: {key: string; name: string}
    variant?: {id: string; name: string}
}

export type LayerPlanContext = {
    key: string
    assignment: Record<string, string>
    parts: VariationPart[]
    layers: LayerPlanLayer[]
}

export type LayerPlanOptionGroup = {
    key: string
    name: string
    variants: {id: string; name: string}[]
}

// The contexts below one combination of gate selections: every combination of the context groups' options is a context, and
// each of them gets a rest layer plus one layer per option of every layer group. Families keep huge plans small.
export type LayerPlanFamily = {
    gateAssignment: Record<string, string>
    gateParts: VariationPart[]
    contextGroups: LayerPlanOptionGroup[]
    layerGroups: LayerPlanOptionGroup[]
    // Groups present here that need no renders of their own: post-processing and groups without visible effect.
    unlayered: string[]
    contextCount: bigint
    partOrder: Record<string, number>
}

export type LayerPlan = {
    groups: LayerPlanGroup[]
    families: LayerPlanFamily[]
    contextCount: bigint
    layerCount: bigint
    warnings: string[]
}

export type LayerPlanOptions<Extra> = {
    sceneOf: (extra: Extra) => readonly SceneNodes.SceneNode[]
    onProgress?: (fraction: number) => void
    isAborted?: () => boolean
    yieldControl?: () => Promise<void>
}

export function layerContextKey(assignment: Readonly<Record<string, string>>) {
    return sortedJSONStringify(assignment)
}

export type ParsedLayerKey = {context: Record<string, string>; group?: string; variant?: string}

const layerKeyPrefix = "layer:"

// Group and variant ids may contain any character, so the whole key is one JSON object to keep it parseable.
export function layerKey(context: Readonly<Record<string, string>>, group?: string, variant?: string) {
    return layerKeyPrefix + sortedJSONStringify(group === undefined ? {context} : {context, group, variant})
}

export function parseLayerKey(key: string): ParsedLayerKey | undefined {
    if (!key.startsWith(layerKeyPrefix)) return undefined
    let parsed: unknown
    try {
        parsed = JSON.parse(key.slice(layerKeyPrefix.length))
    } catch {
        return undefined
    }
    if (typeof parsed !== "object" || parsed === null) return undefined
    const {context, group, variant} = parsed as Record<string, unknown>
    if (typeof context !== "object" || context === null || !Object.values(context).every((value) => typeof value === "string")) return undefined
    if (group === undefined && variant === undefined) return {context: context as Record<string, string>}
    if (typeof group !== "string" || typeof variant !== "string") return undefined
    return {context: context as Record<string, string>, group, variant}
}

function optionCombinations(groups: readonly LayerPlanOptionGroup[]) {
    return groups.reduce((product, group) => product * BigInt(group.variants.length), 1n)
}

export function familyLayerCount(family: LayerPlanFamily) {
    return family.contextCount * BigInt(1 + family.layerGroups.reduce((sum, group) => sum + group.variants.length, 0))
}

function contextParts(family: LayerPlanFamily, selection: Readonly<Record<string, string>>): VariationPart[] {
    return [
        ...family.gateParts,
        ...family.contextGroups.map((group) => ({
            key: group.key,
            group: group.name,
            variant: group.variants.find((v) => v.id === selection[group.key])!.name,
            variantId: selection[group.key],
        })),
    ].sort((a, b) => byOrder(family.partOrder[a.key] ?? Infinity, family.partOrder[b.key] ?? Infinity))
}

function contextOf(family: LayerPlanFamily, selection: Readonly<Record<string, string>>): LayerPlanContext {
    const assignment = {...family.gateAssignment, ...selection}
    const parts = contextParts(family, selection)
    const layers: LayerPlanLayer[] = [{key: layerKey(assignment)}]
    for (const group of family.layerGroups) {
        for (const variant of group.variants)
            layers.push({key: layerKey(assignment, group.key, variant.id), group: {key: group.key, name: group.name}, variant})
    }
    return {key: layerContextKey(assignment), assignment, parts, layers}
}

// Mixed radix over the context groups, the last group changing fastest.
function contextSelection(family: LayerPlanFamily, index: bigint): Record<string, string> {
    const selection: Record<string, string> = {}
    let rest = index
    for (let i = family.contextGroups.length - 1; i >= 0; i--) {
        const group = family.contextGroups[i]
        const size = BigInt(group.variants.length)
        selection[group.key] = group.variants[Number(rest % size)].id
        rest /= size
    }
    return selection
}

export function familyContext(family: LayerPlanFamily, index: bigint): LayerPlanContext {
    return contextOf(family, contextSelection(family, index))
}

export function familyContextParts(family: LayerPlanFamily, index: bigint): VariationPart[] {
    return contextParts(family, contextSelection(family, index))
}

// The context a configuration renders from; configurations list every present group, so extra keys are expected.
export function findLayerPlanContext(plan: LayerPlan, configuration: Readonly<Record<string, unknown>>): LayerPlanContext | undefined {
    for (const family of plan.families) {
        if (!Object.entries(family.gateAssignment).every(([key, variantId]) => configuration[key] === variantId)) continue
        const selection: Record<string, string> = {}
        for (const group of family.contextGroups) {
            const value = configuration[group.key]
            if (typeof value !== "string" || !group.variants.some((variant) => variant.id === value)) return undefined
            selection[group.key] = value
        }
        return contextOf(family, selection)
    }
    return undefined
}

export function findLayerPlanLayer(plan: LayerPlan, key: string): {context: LayerPlanContext; layer: LayerPlanLayer} | undefined {
    const parsed = parseLayerKey(key)
    if (!parsed) return undefined
    const context = findLayerPlanContext(plan, parsed.context)
    if (!context || Object.keys(context.assignment).length !== Object.keys(parsed.context).length) return undefined
    const layer = context.layers.find((candidate) => candidate.key === key)
    return layer ? {context, layer} : undefined
}

// Same semantics as variation filters: an entry needs at least one of its groups present, and every present one restricted.
export function filterLayerPlanFamily(family: LayerPlanFamily, filter: VariationFilter): LayerPlanFamily | undefined {
    let {contextGroups, layerGroups} = family
    const restrict = (groups: LayerPlanOptionGroup[], key: string, allowed: readonly string[]) =>
        groups.map((group) => (group.key === key ? {...group, variants: group.variants.filter((variant) => allowed.includes(variant.id))} : group))
    for (const entry of filter) {
        let present = false
        for (const [key, allowed] of Object.entries(entry)) {
            const value = family.gateAssignment[key]
            if (value !== undefined) {
                if (!allowed.includes(value)) return undefined
            } else if (contextGroups.some((group) => group.key === key)) {
                contextGroups = restrict(contextGroups, key, allowed)
            } else if (layerGroups.some((group) => group.key === key)) {
                layerGroups = restrict(layerGroups, key, allowed)
            } else if (!family.unlayered.includes(key)) {
                continue
            }
            present = true
        }
        if (!present) return undefined
    }
    if (contextGroups === family.contextGroups && layerGroups === family.layerGroups) return family
    if ([...contextGroups, ...layerGroups].some((group) => group.variants.length === 0)) return undefined
    return {...family, contextGroups, layerGroups, contextCount: optionCombinations(contextGroups)}
}

type Classification = {role: LayerGroupRole; reasons: Iterable<string>; effects: Set<SceneEffect>; materialSlots: Set<string>}

const effectOrder: SceneEffect[] = ["geometry", "camera", "lights", "environment", "scene options", "render settings"]

const defaultYield = () => new Promise<void>((resolve) => setTimeout(resolve, 0))
// Compiles can complete without a macrotask in between, so without explicit yields the whole plan runs as one long task.
const yieldIntervalMs = 30

// Groups that only swap materials become layers; anything that changes geometry or the scene joins the context, because a
// layer is only valid against the geometry it was rendered with.
export async function computeLayerPlan<Extra>(
    oracle: VariationOracle<Extra>,
    structure: VariationStructure,
    options: LayerPlanOptions<Extra>,
): Promise<LayerPlan> {
    const {sceneOf, onProgress, isAborted = () => false, yieldControl = defaultYield} = options
    let lastYield = Date.now()
    const evaluate = async (assignment: Record<string, string>) => {
        if (Date.now() - lastYield > yieldIntervalMs) {
            await yieldControl()
            lastYield = Date.now()
        }
        return evaluateContext(oracle, assignment)
    }
    const groups = new Map<
        string,
        {
            name: string
            order?: number
            sectionTitle?: string
            roles: Set<LayerGroupRole>
            reasons: Map<LayerGroupRole, Set<string>>
            structureEffects: Set<LayerGroupRole>
            effects: Set<SceneEffect>
        }
    >()
    const groupNames = new Map<string, string>()
    const families: LayerPlanFamily[] = []
    const warnings = new Set<string>()
    const unstable = new Set<string>()
    const structureGates = new Set<string>()
    const onlyInFirst = new Set<string>()
    const onlyInSecond = new Set<string>()
    let reported = 0
    const report = (fraction: number) => {
        if (!onProgress || fraction <= reported) return
        reported = fraction
        onProgress(fraction)
    }
    const noteRole = (state: ContextState<Extra>, key: string, role: LayerGroupRole, reasons: Iterable<string> = []) => {
        let entry = groups.get(key)
        if (!entry) {
            const {name, order, sectionTitle} = state.byKey.get(key)!.props
            entry = {name, order, sectionTitle, roles: new Set(), reasons: new Map(), structureEffects: new Set(), effects: new Set()}
            groups.set(key, entry)
        }
        entry.roles.add(role)
        addReasons(entry.reasons, role, reasons)
    }
    const addReasons = (reasons: Map<LayerGroupRole, Set<string>>, role: LayerGroupRole, added: Iterable<string>) => {
        const roleReasons = reasons.get(role) ?? new Set()
        for (const reason of added) roleReasons.add(reason)
        reasons.set(role, roleReasons)
    }

    const classify = async (state: ContextState<Extra>, assignment: Record<string, string>, key: string): Promise<Classification> => {
        const base = sceneOf(state.extra)
        const materialSlots = new Set<string>()
        const reasons = new Set<string>()
        const materialObjects = new Set<string>()
        const effects = new Set<SceneEffect>()
        let geometry = false
        let scene = false
        let postProcessing = false
        for (const variantId of permutableVariantIds(state.byKey.get(key)!)) {
            if (variantId === state.byKey.get(key)!.props.value?.id) continue
            if (isAborted()) throw new Error("Layer plan aborted")
            const other = await evaluate({...assignment, [key]: variantId})
            const difference = compareScenes(base, sceneOf(other.extra))
            geometry ||= difference.geometry
            scene ||= difference.scene
            postProcessing ||= difference.postProcessing
            for (const effect of difference.effects) effects.add(effect)
            for (const reason of difference.reasons) reasons.add(reason)
            for (const slot of difference.materialSlots) materialSlots.add(slot)
            for (const object of difference.materialObjects) materialObjects.add(object)
        }
        const role: LayerGroupRole = geometry
            ? "geometry"
            : scene
              ? "scene"
              : materialSlots.size > 0
                ? "material"
                : postProcessing
                  ? "post-processing"
                  : "no effect"
        return {role, reasons: role === "material" ? materialObjects : reasons, effects, materialSlots}
    }

    const walk = async (assignment: Record<string, string>, from: number, to: number): Promise<void> => {
        if (isAborted()) throw new Error("Layer plan aborted")
        const state = await evaluate(assignment)
        for (const configInfo of state.configInfos) groupNames.set(configInfo.props.id, configInfo.props.name)
        const free = unassignedFreeKeys(state, assignment)
        checkKeysAreKnown(structure, free)
        const gates = free.filter((key) => structure.isGate(key))

        if (gates.length > 0) {
            const gate = choosableGate(structure, gates)
            noteRole(state, gate, "structure")
            structureGates.add(gate)
            const effect = await classify(state, assignment, gate)
            const entry = groups.get(gate)!
            entry.structureEffects.add(effect.role)
            for (const sceneEffect of effect.effects) entry.effects.add(sceneEffect)
            addReasons(entry.reasons, effect.role, effect.reasons)
            const variantIds = permutableVariantIds(state.byKey.get(gate)!)
            const share = (to - from) / Math.max(1, variantIds.length)
            for (const [index, variantId] of variantIds.entries())
                await walk({...assignment, [gate]: variantId}, from + index * share, from + (index + 1) * share)
            return
        }

        // Setting a group to its default compiles the same configuration again, which shows whether compiles are reproducible.
        if (free.length > 0) {
            const again = await evaluate({...assignment, [free[0]]: state.byKey.get(free[0])!.props.value!.id})
            const noise = compareScenes(sceneOf(state.extra), sceneOf(again.extra))
            for (const reason of noise.reasons) unstable.add(reason)
            for (const id of noise.onlyInBase) if (onlyInFirst.size < 3) onlyInFirst.add(id)
            for (const id of noise.onlyInOther) if (onlyInSecond.size < 3) onlyInSecond.add(id)
            if (noise.materialSlots.size > 0) unstable.add("materials")
        }

        const classifications = new Map<string, Classification>()
        for (const [index, key] of free.entries()) {
            const classification = await classify(state, assignment, key)
            classifications.set(key, classification)
            noteRole(state, key, classification.role, classification.reasons)
            for (const effect of classification.effects) groups.get(key)!.effects.add(effect)
            report(from + ((index + 1) / Math.max(1, free.length)) * (to - from))
        }

        const contextLeaves = free.filter((key) => ["geometry", "scene"].includes(classifications.get(key)!.role))
        const layerLeaves = free.filter((key) => classifications.get(key)!.role === "material")
        const unlayered = free.filter((key) => ["post-processing", "no effect"].includes(classifications.get(key)!.role))
        for (let i = 0; i < layerLeaves.length; i++) {
            for (let j = i + 1; j < layerLeaves.length; j++) {
                const a = classifications.get(layerLeaves[i])!.materialSlots
                const b = classifications.get(layerLeaves[j])!.materialSlots
                if ([...a].some((slot) => b.has(slot))) {
                    const names = [layerLeaves[i], layerLeaves[j]].map((key) => state.byKey.get(key)!.props.name)
                    warnings.add(`${names[0]} and ${names[1]} change the same part, so their layers cannot be combined independently`)
                }
            }
        }

        const optionGroup = (key: string): LayerPlanOptionGroup => {
            const configInfo = state.byKey.get(key)!
            return {
                key,
                name: configInfo.props.name,
                variants: permutableVariantIds(configInfo).map((id) => ({id, name: configInfo.props.variants.find((v) => v.id === id)?.name ?? id})),
            }
        }
        const gateKeys = new Set(Object.keys(assignment))
        const rank = new Map(configuratorOrder(state.configInfos, (configInfo) => configInfo.props).map((configInfo, index) => [configInfo.props.id, index]))
        const sorted = (keys: Iterable<string>) => [...keys].sort((a, b) => byOrder(rank.get(a) ?? Infinity, rank.get(b) ?? Infinity))
        const contextGroups = sorted(contextLeaves).map(optionGroup)
        families.push({
            gateAssignment: assignment,
            gateParts: configurationParts(state.configInfos, assignment).filter((part) => gateKeys.has(part.key)),
            contextGroups,
            layerGroups: sorted(layerLeaves).map(optionGroup),
            unlayered,
            contextCount: optionCombinations(contextGroups),
            partOrder: Object.fromEntries(sorted([...gateKeys, ...contextLeaves]).map((key, index) => [key, index])),
        })
        report(to)
    }

    await walk({}, 0, 1)
    // Names are resolved at the end because the groups a gate decides about only exist below some of its options.
    for (const gate of structureGates) {
        const decided = [...structure.influence(gate)].filter((key) => key !== gate && groupNames.has(key)).map((key) => groupNames.get(key)!)
        groups.get(gate)!.reasons.set("structure", new Set(decided))
    }
    if (unstable.size > 0) {
        const sample = (label: string, ids: Set<string>) => (ids.size > 0 ? `; ${label}: ${[...ids].join(", ")}` : "")
        const objects = sample("only in the first compile", onlyInFirst) + sample("only in the second", onlyInSecond)
        warnings.add(
            `Compiling the same configuration twice gives different scenes (${[...unstable].sort().join("; ")}${objects}), so groups can be misclassified`,
        )
    }
    for (const [key, group] of groups) {
        if (!group.roles.has("material")) continue
        const objects = structure.sceneObjectsFedBy(key)
        if (objects.length === 0) continue
        const shown = objects.length > 3 ? `${objects.slice(0, 3).join(", ")}, …` : objects.join(", ")
        warnings.add(
            `${group.name} only swaps materials on its own, but also feeds ${shown}; combined with other options it can change them, which its layers would miss`,
        )
    }
    return {
        groups: configuratorOrder(groups, ([, group]) => group).map(([key, {name, roles, reasons, structureEffects, effects}]) => ({
            key,
            name,
            roles: [...roles],
            reasons: Object.fromEntries([...reasons].map(([role, roleReasons]) => [role, [...roleReasons].sort()])),
            structureEffects: [...structureEffects],
            effects: effectOrder.filter((effect) => effects.has(effect)),
        })),
        families,
        contextCount: families.reduce((sum, family) => sum + family.contextCount, 0n),
        layerCount: families.reduce((sum, family) => sum + familyLayerCount(family), 0n),
        warnings: [...warnings],
    }
}
